// Libraries
import * as React from "react";
import { observer } from "mobx-react";
import {
  airportMarkerClosure,
  Filter,
  FilterOption,
  GoogleMapCoordinates,
  MapCenter,
  MapPropertyCardProps,
  Marker,
  MarkerProps,
  placeMarkerClosure,
  PropertyForMap,
  transitMarkerClosure,
  UitkExperimentaModuleMap,
  EventCallback,
} from "uitk-react-experimental-module-map";
// Utils
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent,
  sendImmediateTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
// Types
import { GeoType } from "src/typings/microserviceModels/hotels-flex-module";
import { LocationType } from "src/components/utility/LocationUtils";
import { MapContextType, ModuleMapProps, PoiForMap } from "./typings";
import { PoiStore } from "src/stores/poiStore/PoiStore";
import MapPoiSheet from "./components/MapPoiSheet";
import { Experiment, isVariantEnabled } from "src/components/utility/experiment";
import { formatText } from "bernie-l10n";

type MapPropertyAndPOICardProps = MapPropertyCardProps & {
  property: PropertyForMap & PoiForMap;
};

export const MapContext = React.createContext({});

export const useMapContext = (): MapContextType => {
  const context: React.Context<MapContextType> | {} = React.useContext(MapContext);

  if (!context || Object.keys(context).length === 0) {
    throw new Error(`Map compound components cannot be rendered outside the Map component`);
  }

  return context as MapContextType;
};

const usePoisPins = (
  regionId: string,
  locale: string,
  poiStore: PoiStore,
  isPoiExperimentEnabled: boolean,
  limit: number
) => {
  React.useEffect(() => {
    const tcsLocale = locale.toUpperCase();
    if (isPoiExperimentEnabled && regionId && !poiStore.getPois().length) {
      poiStore.fetchPoisForRegion(regionId.toString(), tcsLocale, limit);
    }
  }, [regionId, poiStore]);
};

/**
 * Main Component Map. Here is rendered the map component.
 * @param {ModuleMapProps} props
 */

const clusterIcon = (count: number, price: string) => {
  return (
    <div className="default uitk-type-200">
      <div
        className="uitk-marker-cluster"
        dangerouslySetInnerHTML={{
          __html: formatText("maps.markers.cluster.available.label", count, price),
        }}
      />
    </div>
  );
};

const displayCard = (onHotelSelect: (selectedProperty: PropertyForMap) => EventCallback | void) => ({
  property,
  isVisible,
}: MapPropertyAndPOICardProps) => {
  const iconType = property.icon && property.icon.type;

  switch (iconType) {
    case "price":
    case "soldOut":
      return (
        <UitkExperimentaModuleMap.PropertyCard
          onHotelSelect={onHotelSelect}
          property={property}
          isVisible={isVisible}
        />
      );
    default:
      return (
        <MapPoiSheet
          id={property.id}
          title={property.name}
          photoUrl={property.imageUrl}
          description={property.description}
          isVisible={isVisible}
        />
      );
  }
};

export const ModuleMap: React.FC<ModuleMapProps> = observer((props: ModuleMapProps) => {
  const { toggleShowFullscreenMap, sendUserToHSR, shouldOpenMapInHSR } = useMapContext();
  const [isVisibleErrorMessage, setIsVisibleErrorMessage] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [center, setCenter] = React.useState({ lat: 0, lng: 0 });

  const location = props.context.searchContext?.location;
  const geoCoordinate = location?.geoCoordinate;
  const filters = props.hotels?.filters || [];
  const regionId = props.context?.searchContext?.location?.id;
  const isPoiExperimentEnabled = isVariantEnabled(props.context, Experiment.HTG_Add_Top_POI_Pins);
  const pois = props.poi && props.poi.getPois();
  const isMobile = props.context && props.context.deviceInformation && props.context.deviceInformation.mobile;

  usePoisPins(regionId, props.context.locale, props.poi, isPoiExperimentEnabled, isMobile ? 5 : 10);

  function onMapMounted(google: any, map: google.maps.Map) {
    const newCenter = map.getCenter();

    if (props.isDynamic) {
      map.setOptions({ scrollwheel: false });
    }

    if (props.onMapMounted) {
      props.onMapMounted();
    }
    setCenter({ lat: newCenter.lat(), lng: newCenter.lng() });

    if (geoCoordinate && geoCoordinate.latitude && geoCoordinate.longitude) {
      initMarkerBasedOnLocationType(location.type, {
        googleMaps: google,
        map,
        position: {
          lat: geoCoordinate.latitude,
          lng: geoCoordinate.longitude,
        },
        label: "",
        id: location.id,
      });
    }
  }

  const initMarkerBasedOnLocationType = (locationType: GeoType, markerProps: MarkerProps): Marker | null => {
    const PlaceMarker = placeMarkerClosure();
    const AirportMarker = airportMarkerClosure();
    const TransitMarker = transitMarkerClosure();

    switch (locationType) {
      case LocationType.POINT_OF_INTEREST:
        return new PlaceMarker({ ...markerProps });
      case LocationType.AIRPORT:
        return new AirportMarker({ ...markerProps });
      case LocationType.TRAIN_STATION:
      case LocationType.METRO_STATION:
        return new TransitMarker({ ...markerProps });
      default:
        return null;
    }
  };

  const mapTracking = (rfrrString: string) => {
    const flexAnalyticsInfo: FlexTrackingInfo = {
      moduleName: props.flexComponent.name,
      action: Action.CLICK,
      rfrr: rfrrString,
    };
    sendDelayedTrackEvent(flexAnalyticsInfo, props.analytics);
  };

  const doSearch = async (mapCenter: MapCenter) => {
    mapTracking("fullscreen.map.search");
    setIsLoading(true);

    if (!props.context || !mapCenter || !mapCenter.lat || !mapCenter.lng) {
      // This shouldn't happen, but let's make sure!
      setIsVisibleErrorMessage(true);
      setIsLoading(false);

      return;
    }

    try {
      await props.hotels.fetchHotelsByLatLong(props.context, mapCenter.lat, mapCenter.lng);
    } catch (err) {
      setIsVisibleErrorMessage(true);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const closeErrorMessage = () => {
    setIsVisibleErrorMessage(false);
  };

  const toolbarHeader = props.context?.searchContext?.location?.extendedName || "";
  // TODO Remove locale, filters and onClose from parent

  const onFiltersDone = async (currentFilters: Filter[]) => {
    const rfrr = `filtering.search.${currentFilters
      .map((option) =>
        option.options
          .map((filterOption) => {
            if (filterOption.isSelected) {
              return `${option.type}-${filterOption.value}`;
            }

            return null;
          })
          .filter((value) => !!value)
          .join(".")
      )
      .filter((value) => !!value)
      .join(".")}`;
    mapTracking(rfrr);

    setIsLoading(true);

    if (!props.context || !center || !center.lat || !center.lng) {
      setIsVisibleErrorMessage(true);
      setIsLoading(false);

      return;
    }

    try {
      await props.hotels.fetchHotelsByLatLong(props.context, center.lat, center.lng);
    } catch (err) {
      setIsVisibleErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const mapFilteringDone = (currentFilters: Filter[]) => {
    onFiltersDone(currentFilters);
  };

  const onMapPan = ({ lat, lng }: GoogleMapCoordinates) => {
    mapTracking("fullscreen.map.pan");
    setCenter({
      lat,
      lng,
    });
  };

  const onClickMarker = (index: number, marker: PropertyForMap | PoiForMap) => {
    if (marker.icon?.type === "place") {
      mapTracking(`fullscreen.marker.poi`);
    } else {
      mapTracking(`fullscreen.marker.${index}`);
    }
  };

  const onMapZoomIn = () => {
    mapTracking("fullscreen.map.zoom.in");
  };

  const onMapZoomOut = () => {
    mapTracking("fullscreen.map.zoom.out");
  };

  const onHotelSelect = () => {
    const flexAnalyticsInfo: FlexTrackingInfo = {
      moduleName: props.flexComponent.name,
      action: Action.CLICK,
      rfrr: "fullscreen.hotel-card",
    };
    sendImmediateTrackEvent(flexAnalyticsInfo, props.analytics);
  };

  const onChangeOption = (updatedOption: FilterOption, type: string) => {
    const flexAnalyticsInfo: FlexTrackingInfo = {
      moduleName: props.flexComponent.name,
      action: Action.CLICK,
      rfrr: `filtering.${type}.${updatedOption.value}`,
    };
    sendDelayedTrackEvent(flexAnalyticsInfo, props.analytics);
  };

  const onClickFilter = (type: string) => {
    const flexTrackingInfo: FlexTrackingInfo = {
      moduleName: "maps",
      action: Action.CLICK,
      rfrr: `filtering.${type}`,
    };
    sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
  };

  if (!props.hotels.allHotelsOnMap.length) {
    return null;
  }

  const getMarkers = () =>
    pois ? ([...props.hotels.allHotelsOnMap, ...pois] as PropertyForMap[]) : props.hotels.allHotelsOnMap;

  return (
    <UitkExperimentaModuleMap
      isVisible={true}
      isVisibleErrorMessage={isVisibleErrorMessage}
      onCloseErrorMessage={closeErrorMessage}
      locale={props.context.locale}
      countryCode={props.context?.site?.country}
      toolbarHeader={toolbarHeader}
      errorHeading={formatText("maps.filter.error.areaError.header")}
      errorMessage={formatText("maps.filter.error.areaError.message")}
      toolbarIconLabel={formatText("maps.fullscreen.close.a11yLabel")}
      properties={getMarkers()}
      onClose={shouldOpenMapInHSR ? sendUserToHSR : toggleShowFullscreenMap}
      onMapMounted={onMapMounted}
      onPan={onMapPan}
      onZoomIn={onMapZoomIn}
      onZoomOut={onMapZoomOut}
      onClickPropertyMarker={onClickMarker}
      initialZoom={13}
      isDynamic={props.isDynamic}
      className={props.classNames}
    >
      {filters.length > 0 ? (
        <UitkExperimentaModuleMap.MapFiltering
          filters={filters}
          onDone={mapFilteringDone}
          doneFiltersText={formatText("maps.filter.done")}
          onChangeOption={onChangeOption}
          onClickFilter={onClickFilter}
        />
      ) : (
        []
      )}
      <UitkExperimentaModuleMap.MapOverlay>
        <UitkExperimentaModuleMap.MapSearch
          isSearchInProgress={isLoading}
          searchMessage={formatText("maps.fullscreen.searchThisArea")}
          onMapSearch={doSearch}
        />
        <UitkExperimentaModuleMap.MapLoader isLoading={isLoading} />
      </UitkExperimentaModuleMap.MapOverlay>
      <UitkExperimentaModuleMap.PropertyCard>{displayCard(onHotelSelect)}</UitkExperimentaModuleMap.PropertyCard>
      <UitkExperimentaModuleMap.ClusteringMarkers
        clusterIcon={clusterIcon}
        minimumClusterSize={3}
        custeringMarkerType={["price"]}
      />
    </UitkExperimentaModuleMap>
  );
});

ModuleMap.displayName = "ModuleMap";

export default ModuleMap;
